/**
 * Transfer actions renderer
 */
import {
  Component,
  ViewEncapsulation,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ITransferData,
  TRANSFER_TYPE,
  COMMON_ACTION_TYPES,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  NotificationType,
  STATUS_ACTION_MAPPING,
  COMMON_ACTION_TYPES_REJECT,
  TRANSFER_PDF_HEADER_VALUES,
} from '@shared/types';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import { Router } from '@angular/router';
import { get, includes, isFunction, forEach, cloneDeep, isEqual } from 'lodash';
import { QohService, StateService, TransferService } from '@shared/services';
import { APIActions, ToolsActions, UiActions } from '@store/actions';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import {
  TRANSFER_ACTION_BUTTONS,
  TRANSFER_ACTION_TYPES,
} from '@shared/types/entities/transfer/transfer-action-buttons.enum';
import { TransferPdfService } from '@app/shared/services/transfer/transfer.pdf.service';
import {
  TRANSFER_FINAL_STATUS,
  TRANSFER_NOT_FINAL_STATUS,
} from '@shared/services/transfer/transfer.definition';
import {
  IPdfDefinitions,
  PdfGenerateServiceV2,
} from '@app/shared/common/make-pdf/v2';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TOASTER_TRANSACTION_FAILED_MESSAGE,
} from '@app/shared/constants';
import { TransferBulkStatusService } from '@app/shared/services/transfer-bulk/transfer-bulk-status.service';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import { addQtyPulledColumn } from '@app/shared/services/transfer/transfer.util';
import { getTotalsDefaultValue } from '@app/shared/services/transfer/transfer-pdf-config';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-actions-renderer',
  templateUrl: 'transfer-actions-renderer.component.html',
  styleUrls: ['transfer-actions-renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVITransferRendererComponent implements AgRendererComponent {
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(TransferBulkStatusService)
  public transferBulkStatusService: TransferBulkStatusService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public data: ITransferData;
  public actionButtons: ActionButton[];
  public hiddenButtons: string[];
  public redirectFunc: Function;
  public refreshFunc: Function;
  public constructor(public injector: Injector) {}

  public agInit(
    params: ICellRendererParams & {
      redirectFunc?: Function;
      hiddenButtons?: string[];
      refreshFunc?: Function;
    },
  ): void {
    this.data = params.node.data;
    this.hiddenButtons = params.hiddenButtons ?? [];
    this.redirectFunc = params.redirectFunc;
    this.refreshFunc = params.refreshFunc;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.transferService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    if (isFunction(this.refreshFunc)) {
      this.refreshFunc();

      return;
    }

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public async edit(forceNotDmSync: boolean = false): Promise<void> {
    const status = this.data?.status;
    const isStatusNeedDmSync = includes(
      this.transferService.needGetUpdateItemsStatus,
      status,
    )
      ? true
      : null;

    if (isFunction(this.redirectFunc)) {
      const modalOptions = this.redirectFunc(
        {
          data: {
            ...this.data,
          },
        },
        'pendingTransferDialog',
      );

      await this.uiActions.formModalDialog(modalOptions);

      return;
    }

    this.router.navigate(
      [this.transferService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: forceNotDmSync ? false : isStatusNeedDmSync,
          showDuplicate: true,
        },
      },
    );
  }

  public async printOrExport(
    type: COMMON_ACTION_TYPES.Print | COMMON_ACTION_TYPES.Export,
  ): Promise<void> {
    const isNotFinalTransfer = this.transferPdfService.isNotFinalTransfer(
      this.data.status,
      this.data.transfer_type,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];

    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: this.data.status,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });
    modalColumns = await this.transferService.getModalColumns(
      this.data.site_id,
      modalColumns,
      this.data.status,
    );

    const pdfConfig = await this.transferService.getPdfConfig(
      (definitions: IPdfDefinitions) =>
        this.transferService.getPdfData(this.data, definitions?.options),
      modalColumns,
      null,
      {
        totalsConfiguration: {
          enabled: true,
          getDefaultValue: () => getTotalsDefaultValue(this.data),
        },
      },
    );
    if (isEqual(type, COMMON_ACTION_TYPES.Export)) {
      await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);
    } else {
      await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
    }
  }

  public async printEmptyColumnPdf(): Promise<void> {
    const isOpenTransfer = isEqual(
      this.data.status,
      TRANSFER_STATUS_VALUE.Open,
    );

    const pdfConfig = await this.transferService.getWrittenSheetPdfConfig(
      (definitions: IPdfDefinitions) =>
        this.transferService.getWrittenSheetPdfData(
          this.data,
          definitions?.options,
        ),
      !isOpenTransfer,
      null,
      {
        showQoh: this.transferService.getDisplayQohFrom(this.data?.site_id),
        isTransferGroup: false,
      },
    );
    await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
  }

  public async voidTransfer(): Promise<void> {
    const updateResult = await this.transferService.bulkVoidHandler([
      this.data,
    ]);

    if (updateResult) {
      this.refresh({});
    }
  }

  public async changeTransferStatus(action: string): Promise<void> {
    const needDmSync = includes(
      [COMMON_ACTION_TYPES.Submit, COMMON_ACTION_TYPES.Accept],
      action,
    );

    const cancelResult =
      await this.transferBulkStatusService.processBulkUpdateStatus(
        [this.data],
        {
          needDmSync,
          action,
          onSingleDmSync:
            needDmSync && !isEqual(COMMON_ACTION_TYPES.Cancel, action)
              ? (): Promise<void> => this.edit(true)
              : null,
        },
      );

    if (cancelResult !== 'Cancel') {
      this.refresh({});
    }
  }

  public duplicate(): void {
    this.transferService.duplicate([this.data]);
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
      case COMMON_ACTION_TYPES.Edit:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Accept:
      case COMMON_ACTION_TYPES.Submit:
      case COMMON_ACTION_TYPES.Cancel:
        this.changeTransferStatus(type);
        break;
      case COMMON_ACTION_TYPES.Reject:
        this.changeTransferStatus(COMMON_ACTION_TYPES_REJECT);
        break;
      case COMMON_ACTION_TYPES.Void:
        this.voidTransfer();
        break;
      case COMMON_ACTION_TYPES.Print:
      case COMMON_ACTION_TYPES.Export:
        this.printOrExport(type);
        break;
      case TRANSFER_ACTION_TYPES.PrintWrittenSheet:
        this.printEmptyColumnPdf();
        break;
      case COMMON_ACTION_TYPES.Duplicate:
        this.duplicate();
        break;
      default:
        break;
    }
  }

  public async changeTransferStatusById(
    status: TRANSFER_STATUS_VALUE,
  ): Promise<void> {
    try {
      this.uiActions.enableLoader();
      await this.transferService
        .updateEntity({ status }, { endpoint: this.data._id })
        .toPromise();
    } catch (e) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    } finally {
      this.uiActions.disableLoader();
      this.refresh({});
    }
    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
        did: this.i18nService.instant(STATUS_ACTION_MAPPING[status]),
      }),
    });
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  protected initActionButtons(): void {
    const isPending: boolean = isEqual(
      TRANSFER_STATUS_VALUE.Pending,
      this.data.status,
    );
    const isOpen: boolean = isEqual(
      TRANSFER_STATUS_VALUE.Open,
      this.data.status,
    );
    const isPreEvent: boolean = isEqual(
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      this.data.transfer_type,
    );
    const isOutsideOrDuring: boolean = includes(
      [
        TRANSFER_TYPE.DURING_EVENT_TRANSFER,
        TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
      ],
      this.data.transfer_type,
    );
    const isDuringOrOutsideOnPending: boolean = isPending && isOutsideOrDuring;

    this.actionButtons = cloneDeep(TRANSFER_ACTION_BUTTONS) as ActionButton[];

    forEach(this.actionButtons, (btn) => {
      const isHiddenBtn = includes(this.hiddenButtons, btn.type);

      if (isHiddenBtn) {
        btn.visible = false;

        return;
      }
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          btn.visible = includes(TRANSFER_FINAL_STATUS, this.data.status);
          break;
        case COMMON_ACTION_TYPES.Edit:
          btn.visible = includes(TRANSFER_NOT_FINAL_STATUS, this.data.status);
          break;
        case COMMON_ACTION_TYPES.Submit:
        case COMMON_ACTION_TYPES.Cancel:
          btn.visible = isEqual(TRANSFER_STATUS_VALUE.Open, this.data.status);
          break;
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
          btn.visible =
            includes(
              [
                TRANSFER_STATUS_VALUE.Canceled,
                TRANSFER_STATUS_VALUE.Completed,
                TRANSFER_STATUS_VALUE.Open,
                TRANSFER_STATUS_VALUE.Void,
              ],
              this.data.status,
            ) ||
            isPreEvent ||
            isOutsideOrDuring;
          break;
        case COMMON_ACTION_TYPES.Reject:
          btn.visible = isPending;
          break;
        case COMMON_ACTION_TYPES.Accept:
          btn.visible = isDuringOrOutsideOnPending;
          break;
        case TRANSFER_ACTION_TYPES.PrintWrittenSheet:
          btn.visible = isPending || isOpen;
          break;
        case COMMON_ACTION_TYPES.Void:
          btn.visible = this.data.visibleVoidBtn;
          break;
        case COMMON_ACTION_TYPES.Duplicate:
          btn.visible = includes(
            [
              TRANSFER_STATUS_VALUE.Open,
              TRANSFER_STATUS_VALUE.Pending,
              TRANSFER_STATUS_VALUE.Completed,
            ],
            this.data.status,
          );
          break;
        default:
          break;
      }
    });
  }
}
